import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trpc } from "../lib/trpc.js";
import { Button } from "../components/ui/Button.js";

export function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: me } = trpc.auth.me.useQuery();

  const { data: preview, isLoading, error } = trpc.invites.preview.useQuery(
    { token: token! },
    { enabled: !!token }
  );

  const accept = trpc.invites.accept.useMutation({
    onSuccess: (result) => {
      navigate(`/clubs/${result.clubId}`, { replace: true });
    },
  });

  const isWrongEmail = !!(preview?.targetEmail && me && me.email !== preview.targetEmail);

  // Auto-accept: fire as soon as we're authenticated and there's no email mismatch.
  // Runs for both post-signup and post-login redirects, and direct navigation.
  useEffect(() => {
    if (me && preview && !isWrongEmail && !accept.isPending && !accept.isSuccess && !accept.isError) {
      accept.mutate({ token: token! });
    }
  }, [me, preview, isWrongEmail]);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
      style={{ background: "var(--color-background)" }}
    >
      <Link
        to="/"
        className="font-serif text-xl font-semibold mb-10"
        style={{ color: "var(--color-foreground)", textDecoration: "none" }}
      >
        {t("nav.logo")}
      </Link>

      <div
        className="w-full max-w-sm rounded-2xl p-8 space-y-6"
        style={{
          background: "var(--color-card)",
          border: "1px solid var(--color-border)",
          boxShadow: "var(--shadow-lg)",
          animation: "slide-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) both",
        }}
      >
        {isLoading && (
          <div className="flex items-center gap-2 justify-center py-4">
            <span
              className="h-4 w-4 rounded-full border-2 border-current border-t-transparent"
              style={{ color: "var(--color-accent)", animation: "spin 0.7s linear infinite" }}
            />
            <span className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>{t("acceptInvite.loading")}</span>
          </div>
        )}

        {error && (
          <div className="text-center space-y-3">
            <p className="font-serif italic text-lg" style={{ color: "var(--color-muted-foreground)" }}>
              {error.message}
            </p>
            <Link to="/dashboard">
              <Button variant="ghost" size="sm">{t("acceptInvite.goToDashboard")}</Button>
            </Link>
          </div>
        )}

        {preview && (
          <>
            <div>
              <p
                className="text-xs font-medium uppercase tracking-widest mb-1"
                style={{ color: "var(--color-accent)" }}
              >
                {t("acceptInvite.youreInvited")}
              </p>
              <h1
                className="font-serif font-semibold"
                style={{ fontSize: "1.5rem", letterSpacing: "-0.02em", color: "var(--color-foreground)" }}
              >
                {preview.clubName}
              </h1>
              {preview.targetEmail && (
                <p className="text-sm mt-1" style={{ color: "var(--color-muted-foreground)" }}>
                  {t("acceptInvite.inviteSentTo", { email: preview.targetEmail })}
                </p>
              )}
            </div>

            {!me ? (
              <div className="space-y-3">
                {preview.targetEmail && (
                  <p className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>
                    {t("acceptInvite.signInToAccept", { email: preview.targetEmail })}
                  </p>
                )}
                <Link to={`/login`} state={{ from: `/invite/${token}` }}>
                  <Button className="w-full">{t("acceptInvite.signIn")}</Button>
                </Link>
                <Link to={`/signup`} state={{ from: `/invite/${token}` }}>
                  <Button variant="outline" className="w-full">{t("acceptInvite.createAccount")}</Button>
                </Link>
              </div>
            ) : preview.targetEmail && me.email !== preview.targetEmail ? (
              <div className="space-y-3">
                <p className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>
                  {t("acceptInvite.signedInAs")} <strong>{me.email}</strong>, {t("acceptInvite.wrongAccount")}{" "}
                  <strong>{preview.targetEmail}</strong>.
                </p>
                <p className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>
                  {t("acceptInvite.switchAccount")}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {accept.isError ? (
                  <>
                    <p className="text-xs" style={{ color: "var(--color-destructive)" }}>
                      {accept.error.message}
                    </p>
                    <Link to="/dashboard">
                      <Button variant="ghost" size="sm" className="w-full">{t("acceptInvite.goToDashboard")}</Button>
                    </Link>
                  </>
                ) : (
                  <div className="flex items-center gap-2 justify-center py-2">
                    <span
                      className="h-4 w-4 rounded-full border-2 border-current border-t-transparent"
                      style={{ color: "var(--color-accent)", animation: "spin 0.7s linear infinite" }}
                    />
                    <span className="text-sm" style={{ color: "var(--color-muted-foreground)" }}>
                      {t("acceptInvite.joining")}
                    </span>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
